import React, { useState } from 'react';
import { CheckCircle, MapPin, AlertCircle } from 'lucide-react';
import { doc, collection, query, where, getDocs, updateDoc, addDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../services/firebase';

const JoinClubForm = ({ user, onJoinSuccess }) => {
  const [clubCode, setClubCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clubFound, setClubFound] = useState(null);
  const [step, setStep] = useState('enter-code'); // enter-code, confirm, registering

  const handleLookupClub = async () => {
    if (!clubCode.trim()) {
      setError('Please enter a club code');
      return;
    }

    setLoading(true);
    setError('');
    setClubFound(null);

    try {
      // Look up all clubs just in case Firestore strict equality fails on whitespace/case/field mismatches
      const clubsRef = collection(db, 'club');
      const snapshot = await getDocs(clubsRef);

      // Normalize input: uppercase, trim, replace O with 0
      const searchCode = clubCode.trim().toUpperCase().replace(/O/g, '0');

      let targetClub = null;

      for (const tDoc of snapshot.docs) {
        const data = tDoc.data();
        
        // Extract and normalize potential codes from DB
        const rawCodes = [data.joinCode, data.code, data.clubCode].filter(Boolean);
        const match = rawCodes.find(c => {
          const normalizedDbCode = String(c).trim().toUpperCase().replace(/O/g, '0');
          return normalizedDbCode === searchCode;
        });

        if (match) {
          targetClub = { id: tDoc.id, ...data };
          break;
        }
      }

      if (!targetClub) {
        setError('Club not found. Please check the code and try again.');
        setLoading(false);
        return;
      }

      setClubFound(targetClub);
      setStep('confirm');
    } catch (err) {
      console.error('Error looking up club:', err);
      setError('Failed to look up club. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClub = async () => {
    if (!clubFound || !user) return;

    setLoading(true);
    setError('');
    setStep('registering');

    try {
      // Prepare user document update
      const updatePayload = {
        [`joinedClubs.${clubFound.id}`]: {
          clubId: clubFound.id,
          clubName: clubFound.name,
          role: 'player',
          status: 'pending',
          joinedAt: new Date().toISOString()
        },
        joinedClubsIds: arrayUnion(clubFound.id)
      };

      // Resolve legacy club robustly so older players don't lose their original club
      const legacyClubId = user.clubId || user.clubInfo?.clubId;
      const legacyClubName = user.clubName || user.clubInfo?.clubName || 'Legacy Club';
      const legacyRole = user.role || user.clubInfo?.role || 'player';

      // Seed legacy club into the arrays if it exists and isn't the same club!
      if (legacyClubId && legacyClubId !== clubFound.id) {
        updatePayload[`joinedClubs.${legacyClubId}`] = user.joinedClubs?.[legacyClubId] || user.clubInfo || {
          clubId: legacyClubId,
          clubName: legacyClubName,
          role: legacyRole,
          status: 'approved' // Legacy club membership implies they are already approved
        };
        updatePayload.joinedClubsIds = arrayUnion(legacyClubId, clubFound.id);
      } else if (!legacyClubId) {
        // Orphan user just joined their first club
        updatePayload.clubId = clubFound.id;
        updatePayload.clubInfo = {
          clubId: clubFound.id,
          clubName: clubFound.name,
          role: 'player',
          status: 'pending', // Requires admin approval
          joinedAt: new Date().toISOString()
        };
      }
      
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, updatePayload);

      // Create a pending player request for admin to approve
      await addDoc(collection(db, 'playerRequests'), {
        clubId: clubFound.id,
        userId: user.uid,
        email: user.email,
        name: user.displayName || user.email?.split('@')[0] || 'Unknown',
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      onJoinSuccess();
    } catch (err) {
      console.error('Error joining club:', err);
      setError('Failed to join club. Please try again.');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'registering') {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Joining {clubFound?.name}...</p>
      </div>
    );
  }

  if (step === 'confirm' && clubFound) {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h3 className="text-xl font-bold text-green-800 mb-2">Club Found!</h3>
          <p className="text-2xl font-bold text-gray-900 mb-1">{clubFound.name}</p>
          {clubFound.location && (
            <p className="text-gray-600 flex items-center justify-center gap-1">
              <MapPin size={16} /> {clubFound.location}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> After joining, a club admin will need to approve your request and assign you to a team.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => {
              setStep('enter-code');
              setClubFound(null);
              setClubCode('');
            }}
            className="flex-1 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleJoinClub}
            disabled={loading}
            className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Joining...' : 'Join Club'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Club Invite Code
        </label>
        <input
          type="text"
          value={clubCode}
          onChange={(e) => {
            setClubCode(e.target.value.toUpperCase());
            setError('');
          }}
          placeholder="Enter code (e.g., ABC123)"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-green-500 focus:border-green-500 uppercase"
          maxLength={10}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <button
        onClick={handleLookupClub}
        disabled={loading || !clubCode.trim()}
        className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Looking up club...
          </span>
        ) : (
          'Find My Club'
        )}
      </button>
    </div>
  );
};

export default JoinClubForm;